import { CompleteKundali } from './kundali';

export enum UserRole {
  SUPER_ADMIN = 'super_admin',
  ADMIN = 'admin', // Super user / user administration (no private kundali data access)
  CONSULTING_ASTROLOGER = 'consulting_astrologer',
  PRO_ASTROLOGER = 'pro_astrologer',
  NOVICE_ASTROLOGER = 'novice_astrologer', // Deprecated: mapped to basic_user or pro_astrologer
  ADVANCED_ASTROLOGY_USER = 'advanced_astrology_user',
  BASIC_USER = 'basic_user',
  GUEST = 'guest',
  TESTER = 'tester',
}

export enum AccountTier {
  GUEST = 'GUEST',
  BASIC = 'BASIC',
  PRO_ASTRO = 'PRO_ASTRO',
  ACHARYA = 'ACHARYA',
}

/**
 * Deterministic normalization boundary for legacy persisted tiers.
 * Maps legacy historical tier identifiers into the canonical 4-tier model.
 * Preserves unmapped values for audit without breaking runtime behavior.
 */
export function normalizeAccountTier(rawTier: unknown): {
  canonicalTier: AccountTier;
  historicalTier?: string;
  migrationStatus: 'CANONICAL' | 'MIGRATED' | 'REQUIRES_REVIEW';
} {
  if (!rawTier || typeof rawTier !== 'string') {
    return { canonicalTier: AccountTier.GUEST, migrationStatus: 'CANONICAL' };
  }
  const clean = rawTier.trim().toUpperCase();
  switch (clean) {
    case 'GUEST':
      return { canonicalTier: AccountTier.GUEST, migrationStatus: 'CANONICAL' };
    case 'FREE':
    case 'FREE_TIER':
      return { canonicalTier: AccountTier.GUEST, historicalTier: clean, migrationStatus: 'MIGRATED' };
    case 'BASIC':
      return { canonicalTier: AccountTier.BASIC, migrationStatus: 'CANONICAL' };
    case 'PRO_ASTRO':
      return { canonicalTier: AccountTier.PRO_ASTRO, migrationStatus: 'CANONICAL' };
    case 'PRO':
    case 'PRO_TIER':
      return { canonicalTier: AccountTier.PRO_ASTRO, historicalTier: clean, migrationStatus: 'MIGRATED' };
    case 'ACHARYA':
      return { canonicalTier: AccountTier.ACHARYA, migrationStatus: 'CANONICAL' };
    case 'PREMIUM':
    case 'ULTRA_PREMIUM':
      return { canonicalTier: AccountTier.ACHARYA, historicalTier: clean, migrationStatus: 'MIGRATED' };
    default:
      return { canonicalTier: AccountTier.BASIC, historicalTier: clean, migrationStatus: 'REQUIRES_REVIEW' };
  }
}

export enum Permission {
  // Profile & Family permissions
  VIEW_OWN_PROFILE = 'VIEW_OWN_PROFILE',
  EDIT_OWN_PROFILE = 'EDIT_OWN_PROFILE',
  DELETE_OWN_PROFILE = 'DELETE_OWN_PROFILE',
  VIEW_FAMILY_PROFILE = 'VIEW_FAMILY_PROFILE',
  CREATE_FAMILY_PROFILE = 'CREATE_FAMILY_PROFILE',
  EDIT_FAMILY_PROFILE = 'EDIT_FAMILY_PROFILE',
  DELETE_FAMILY_PROFILE = 'DELETE_FAMILY_PROFILE',

  // Kundali permissions
  CREATE_KUNDALI = 'CREATE_KUNDALI',
  VIEW_OWN_KUNDALI = 'VIEW_OWN_KUNDALI',
  VIEW_FAMILY_KUNDALI = 'VIEW_FAMILY_KUNDALI',
  PRINT_KUNDALI = 'PRINT_KUNDALI',
  SAVE_REPORT = 'SAVE_REPORT',
  EXPORT_KUNDALI = 'EXPORT_KUNDALI',

  // Astrological & Analysis depth permissions
  BASIC_PREDICTIONS = 'BASIC_PREDICTIONS',
  BASIC_AI_QUESTION = 'BASIC_AI_QUESTION',
  DETAILED_AI_ANALYSIS = 'DETAILED_AI_ANALYSIS',
  TECHNICAL_REASONING = 'TECHNICAL_REASONING',
  ADVANCED_CALCULATIONS = 'ADVANCED_CALCULATIONS', // D1-D60, Ashtakvarga, Shadbala, etc.
  LEARNING_EXPLANATIONS = 'LEARNING_EXPLANATIONS',

  // Professional & Consultant permissions
  CONSULTATION_ACCESS = 'CONSULTATION_ACCESS',
  ASSIGN_CONSULTATION = 'ASSIGN_CONSULTATION',
  VIEW_ASSIGNED_CUSTOMER = 'VIEW_ASSIGNED_CUSTOMER',
  PRO_CALCULATIONS = 'PRO_CALCULATIONS',
  PRACTICE_PROFILES = 'PRACTICE_PROFILES',

  // Administration permissions
  USER_ADMINISTRATION = 'USER_ADMINISTRATION',
  BAN_USER = 'BAN_USER',
  RESET_USER_CREDENTIALS = 'RESET_USER_CREDENTIALS',
  MANAGE_ROLES = 'MANAGE_ROLES',
  MANAGE_ENTITLEMENTS = 'MANAGE_ENTITLEMENTS',
  KUNDALI_ADMINISTRATION = 'KUNDALI_ADMINISTRATION', // SUPER_ADMIN only
  SYSTEM_CONFIGURATION = 'SYSTEM_CONFIGURATION',
  AUDIT_LOG_ACCESS = 'AUDIT_LOG_ACCESS',
  DATA_RECOVERY = 'DATA_RECOVERY',
}

export interface BanRecord {
  userId: string;
  email: string;
  phone?: string;
  bannedAt: string; // ISO string
  bannedBy: string; // Super Admin UID
  reason: string;
  isBanned: boolean;
}

export interface UserProfile {
  uid: string;
  email: string;
  displayName: string;
  photoURL?: string;
  mobile?: string;
  phone?: string;
  role: UserRole; // Active/Simulated role for application components
  baseRole?: UserRole; // True authenticated identity role
  activeRole?: UserRole; // Currently simulated role
  assignedRoles?: UserRole[]; // Officially assigned roles
  allowedTestRoles?: UserRole[]; // Permitted testing roles for Tester
  isTester?: boolean; // Tester authorization flag
  testMode?: boolean; // Switched testing mode flag
  tier: AccountTier;
  historicalTier?: string;
  tierMigrationStatus?: 'CANONICAL' | 'MIGRATED' | 'REQUIRES_REVIEW';
  hasPracticeEntitlement: boolean;
  hasConsultationEntitlement: boolean;
  kundaliQuota?: number;
  authProvider: 'google' | 'email-otp' | 'mobile-otp' | 'password' | 'custom';
  createdAt: string;
  updatedAt?: string;
  isDeleted?: boolean;
  deletedAt?: string;
  isBanned?: boolean;
  bannedAt?: string;
  bannedBy?: string;
  banReason?: string;
  sessionToken?: string;
  token?: string;
}

export type ProfileRelationship =
  | 'self'
  | 'spouse'
  | 'child'
  | 'parent'
  | 'sibling'
  | 'friend'
  | 'client'
  | 'practice';

export interface UserProfileRecord {
  profileId: string;
  ownerUserId: string; // The User who created/owns this profile
  name: string;
  relationship: ProfileRelationship;
  gender: 'male' | 'female' | 'other';
  date: string;
  time: string;
  placeName: string;
  latitude: number;
  longitude: number;
  timezone: number;
  notes?: string;
  isPractice?: boolean;
  isDeleted?: boolean;
  deletedAt?: string;
  createdAt: string;
  updatedAt: string;
}

export interface ConsultationRecord {
  consultationId: string;
  consultantAstrologerId: string;
  customerUserId: string;
  customerDisplayName: string;
  authorizedProfileId: string;
  authorizedKundaliId: string;
  scope: 'full_reading' | 'dasha_analysis' | 'remedies' | 'muhurta' | 'general_query';
  startTime: string;
  expiryTime: string;
  status: 'active' | 'completed' | 'revoked' | 'expired';
  customerQuestions?: string[];
  astrologerNotes?: string;
  createdAt: string;
  updatedAt: string;
}

export interface PracticeKundaliRecord {
  id: string;
  title: string;
  category: 'Famous Chart' | 'Yoga Study' | 'Dasha Timing' | 'Varga Analysis' | 'Medical Jyotish';
  learningObjective: string;
  birthDetails: {
    name: string;
    gender: string;
    date: string;
    time: string;
    placeName: string;
    latitude: number;
    longitude: number;
    timezoneOffsetMinutes: number;
  };
  notes: string;
  isPracticeData: true;
}

export type AuditEventType =
  | 'ROLE_CHANGE'
  | 'ENTITLEMENT_CHANGE'
  | 'SUPER_ADMIN_ACCESS'
  | 'ADMIN_USER_MAINTENANCE'
  | 'CONSULTANT_ACCESS'
  | 'CONSULTATION_ASSIGNED'
  | 'CONSULTATION_REVOKED'
  | 'PROFILE_CREATED'
  | 'PROFILE_UPDATED'
  | 'PROFILE_DELETED'
  | 'KUNDALI_SAVED'
  | 'KUNDALI_EXPORTED'
  | 'KUNDALI_DELETED'
  | 'DATA_RESTORED'
  | 'AUTHZ_ALLOWED'
  | 'AUTHZ_DENIED'
  | 'PROMPT_INJECTION_BLOCKED'
  | 'GUEST_QUOTA_EXCEEDED'
  | 'USER_BANNED'
  | 'USER_UNBANNED'
  | 'IDENTIFIER_BLACKLISTED'
  | 'BANNED_LOGIN_BLOCKED';

export interface AuditLogRecord {
  eventId: string;
  timestamp: string;
  eventType: AuditEventType;
  userId: string;
  userEmail: string;
  userRole: UserRole | string;
  resourceType: 'user' | 'profile' | 'kundali' | 'consultation' | 'system' | 'ai_chat';
  resourceId?: string;
  action: string;
  decision: 'ALLOW' | 'DENY';
  reason?: string;
  details: string;
  integrityHash: string;
}
